<template>
    <el-card>
        <div class="product-size-container">
            <!-- 左侧：产品类型 / 模板树（按产品型号分组） -->
            <div class="type-panel">
                <div class="type-panel-header">
                    <span class="type-panel-title">产品类型</span>
                    <el-button type="text" size="mini" icon="el-icon-plus" class="new-type-btn"
                        @click="goProductModel">新建设备类型</el-button>
                </div>
                <el-empty v-if="templateList.length === 0 && !templateLoading" description="暂无模板产品" />
                <div v-else class="template-scroll" @scroll="handleTemplateScroll">
                    <div v-for="group in typeGroups" :key="group.key" class="type-group">
                        <!-- 类型分组头（点击展开/收起） -->
                        <div class="type-group-header" @click="toggleGroup(group.key)">
                            <span class="type-group-name">{{ group.name }}</span>
                            <i :class="isGroupExpanded(group.key) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                        </div>
                        <!-- 分组下的模板 -->
                        <template v-if="isGroupExpanded(group.key)">
                            <div v-for="template in group.templates" :key="String(template.id)"
                                class="template-row"
                                :class="{ active: String(template.id) === String(activeTemplate) }"
                                @click="handleTemplateSelect(template.id)">
                                <span class="template-name">{{ template.templateName }}</span>
                                <span class="template-actions" @click.stop>
                                    <el-button type="text" size="mini" class="tpl-del"
                                        @click="handleTemplateDelete(template)">删除</el-button>
                                    <el-button type="text" size="mini" class="tpl-edit"
                                        @click="openTemplateEditDialog(template)">编辑</el-button>
                                </span>
                            </div>
                            <div class="template-add" @click="openTemplateAddDialog(group)">新增模板</div>
                        </template>
                    </div>
                    <div class="load-more">
                        <el-button v-if="hasMoreTemplates && !templateLoading" type="text" size="mini"
                            @click="loadMoreTemplates">加载更多</el-button>
                        <span v-else-if="templateLoading">加载中...</span>
                        <span v-else-if="templateList.length">已加载全部 {{ templateTotal }} 个</span>
                    </div>
                </div>
            </div>

            <!-- 右侧：模板配置 -->
            <div class="config-panel">
                <div class="config-title">模板配置</div>
                <div class="config-tabs">
                    <span class="config-tab" :class="{ active: activeTab === 'size' }"
                        @click="switchTab('size')">产品尺寸</span>
                    <span class="config-tab" :class="{ active: activeTab === 'config' }"
                        @click="switchTab('config')">产品配置</span>
                </div>

                <!-- 产品尺寸 -->
                <div v-show="activeTab === 'size'">
                    <div class="pane-toolbar">
                        <el-button type="primary" size="small" :disabled="!activeTemplate"
                            @click="openAddDialog">添加尺寸</el-button>
                    </div>
                    <el-empty v-if="!activeTemplate" description="请先选择左侧模板" />
                    <el-empty v-else-if="tableData.length === 0 && !sizeLoading" description="暂无尺寸数据" />
                    <el-table v-else :data="tableData" border stripe v-loading="sizeLoading"
                        style="width: 100%" :fit="true">
                        <el-table-column label="尺寸(m)" align="center" min-width="110">
                            <template slot-scope="scope">
                                {{ (sizeOf(scope.row) && sizeOf(scope.row).name) || '--' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="规格（长×宽×高 m）" align="center" min-width="170">
                            <template slot-scope="scope">
                                <span v-if="sizeOf(scope.row)">
                                    {{ sizeOf(scope.row).length }} × {{ sizeOf(scope.row).width }} × {{ sizeOf(scope.row).height }}
                                </span>
                                <span v-else>--</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="对外价格(元)" align="right" min-width="110">
                            <template slot-scope="scope">
                                {{ sizeOf(scope.row) ? formatPrice(sizeOf(scope.row).basePriceOut) : '--' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="内部价格(元)" align="right" min-width="110">
                            <template slot-scope="scope">
                                {{ sizeOf(scope.row) ? formatPrice(sizeOf(scope.row).basePriceInner) : '--' }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ scope.row.remark || '--' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="120" align="center" fixed="right">
                            <template slot-scope="scope">
                                <el-button type="text" size="small" style="color: #f56c6c"
                                    @click="handleDelete(scope.row)">删除</el-button>
                                <el-button type="text" size="small"
                                    @click="openEditDialog(scope.row)">编辑</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-pagination
                        v-if="activeTemplate && currentTotal > 0"
                        class="pagination"
                        background
                        layout="total, sizes, prev, pager, next, jumper"
                        :current-page="currentPage"
                        :page-size="pageSize"
                        :page-sizes="[10, 20, 50, 100]"
                        :total="currentTotal"
                        @size-change="handleSizeChange"
                        @current-change="handleCurrentChange"
                    />
                </div>

                <!-- 产品配置 -->
                <div v-show="activeTab === 'config'">
                    <div class="pane-toolbar cfg-toolbar">
                        <el-button type="primary" size="small" :disabled="!activeTemplate"
                            @click="openCfgAddDialog">添加配置</el-button>
                        <el-select v-model="cfgGroupFilter" placeholder="全部配置分组" clearable size="small"
                            :disabled="!activeTemplate" style="width: 180px"
                            @change="handleCfgGroupFilterChange">
                            <el-option v-for="g in groupOptions" :key="g.id" :value="g.id"
                                :label="g.name || g.code" />
                        </el-select>
                    </div>
                    <el-empty v-if="!activeTemplate" description="请先选择左侧模板" />
                    <el-empty v-else-if="cfgData.length === 0 && !cfgLoading" description="暂无产品配置" />
                    <el-table v-else :data="cfgData" border stripe v-loading="cfgLoading"
                        style="width: 100%" :fit="true">
                        <el-table-column label="配置分组" min-width="130" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ groupMap[scope.row.groupId] ? groupMap[scope.row.groupId].name : (scope.row.groupId || '--') }}
                            </template>
                        </el-table-column>
                        <el-table-column label="配置子项" min-width="140" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ itemMap[scope.row.itemId] ? (itemMap[scope.row.itemId].itemName || itemMap[scope.row.itemId].name) : (scope.row.itemId || '--') }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="defaultQty" label="默认数量" width="90" align="center" />
                        <el-table-column prop="minQty" label="最小数量" width="90" align="center" />
                        <el-table-column prop="maxQty" label="最大数量" width="90" align="center" />
                        <el-table-column label="状态" width="90" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
                                    {{ scope.row.status === 1 ? '已激活' : '停用' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="120" align="center" fixed="right">
                            <template slot-scope="scope">
                                <el-button type="text" size="small" style="color: #f56c6c"
                                    @click="handleCfgDelete(scope.row)">删除</el-button>
                                <el-button type="text" size="small"
                                    @click="openCfgEditDialog(scope.row)">编辑</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-pagination
                        v-if="activeTemplate && cfgTotal > 0"
                        class="pagination"
                        background
                        layout="total, sizes, prev, pager, next, jumper"
                        :current-page="cfgPage"
                        :page-size="cfgSize"
                        :page-sizes="[10, 20, 50, 100]"
                        :total="cfgTotal"
                        @size-change="handleCfgSizeChange"
                        @current-change="handleCfgCurrentChange"
                    />
                </div>
            </div>
        </div>

        <!-- 新增/编辑尺寸弹窗 -->
        <el-dialog :title="editId ? '编辑模板产品尺寸' : '新增模板产品尺寸'" :visible.sync="addVisible" width="560px"
            :close-on-click-modal="false" @closed="resetAddForm">
            <el-form ref="addFormRef" :model="addForm" :rules="addRules" label-width="110px">
                <el-form-item label="所属模板">
                    <el-input :value="activeTemplateName" disabled />
                </el-form-item>
                <!-- 新增：尺寸多选，批量添加 -->
                <el-form-item v-if="!editId" label="产品尺寸" prop="sizeIds">
                    <el-select ref="sizeSelectRef" v-model="addForm.sizeIds" multiple placeholder="请选择尺寸（可多选）"
                        style="width: 100%">
                        <el-option v-for="item in sizeOptions" :key="item.id" :value="item.id" :label="item.name">
                            <div style="display: flex; align-items: center; justify-content: space-between">
                                <span>{{ item.name }}</span>
                                <span style="color: #9ca3af; font-size: 12px">{{ item.code }}</span>
                            </div>
                        </el-option>
                        <!-- 下拉底部：全选 / 全部删除 -->
                        <div class="size-select-footer">
                            <span class="size-select-count">已选 {{ addForm.sizeIds.length }} 项</span>
                            <div class="size-select-actions">
                                <el-button type="text" size="mini" @click="selectAllSizes">全选</el-button>
                                <el-button type="text" size="mini" class="size-clear-btn"
                                    @click="clearAllSizes">全部删除</el-button>
                            </div>
                        </div>
                    </el-select>
                </el-form-item>
                <!-- 编辑：单条记录，尺寸单选 -->
                <el-form-item v-else label="产品尺寸" prop="sizeId">
                    <el-select v-model="addForm.sizeId" placeholder="请选择尺寸" style="width: 100%">
                        <el-option v-for="item in sizeOptions" :key="item.id" :value="item.id" :label="item.name">
                            <div style="display: flex; align-items: center; justify-content: space-between">
                                <span>{{ item.name }}</span>
                                <span style="color: #9ca3af; font-size: 12px">{{ item.code }}</span>
                            </div>
                        </el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="备注" prop="remark">
                    <el-input v-model="addForm.remark" type="textarea" :rows="2" placeholder="请输入备注"
                        maxlength="200" show-word-limit />
                </el-form-item>
                <el-form-item label="排序" prop="sortOrder">
                    <el-input-number v-model="addForm.sortOrder" :min="0" :max="9999" controls-position="right" />
                </el-form-item>
                <el-form-item label="状态" prop="status">
                    <el-switch v-model="addForm.status" :active-value="1" :inactive-value="0"
                        active-text="激活" inactive-text="停用" />
                </el-form-item>
            </el-form>
            <div slot="footer" style="display: flex; justify-content: flex-end; gap: 6px">
                <el-button @click="addVisible = false">取消</el-button>
                <el-button type="primary" :loading="addLoading" @click="handleSaveSubmit">保存</el-button>
            </div>
        </el-dialog>

        <!-- 新增/编辑配置弹窗 -->
        <el-dialog :title="cfgEditId ? '编辑产品配置' : '新增产品配置'" :visible.sync="cfgVisible" width="900px"
            :close-on-click-modal="false" custom-class="cfg-dialog" @closed="resetCfgForm">
            <el-form ref="cfgFormRef" :model="cfgForm" :rules="cfgRules" label-width="110px">
                <el-form-item label="所属模板">
                    <el-input :value="activeTemplateName" disabled />
                </el-form-item>

                <!-- 新增：卡片组，每张卡片一个分组，子项可多选 -->
                <template v-if="!cfgEditId">
                    <el-divider content-position="center">产品配置</el-divider>
                    <div v-for="(row, index) in cfgRows" :key="index" class="cfg-card">
                        <div class="cfg-card-header">
                            <span>配置分组 {{ index + 1 }}</span>
                            <el-button type="text" size="mini" class="cfg-card-del"
                                @click="removeCfgRow(index)">删除</el-button>
                        </div>
                        <el-form-item label="配置分组" label-width="90px">
                            <el-select v-model="row.groupId" placeholder="选择分组" size="small"
                                style="width: 100%" @change="handleCfgRowGroupChange(row)">
                                <el-option v-for="g in groupOptions" :key="g.id" :value="g.id"
                                    :label="g.name || g.code" :disabled="isCfgGroupDisabled(g.id, row)" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="配置子项" label-width="90px">
                            <el-select v-model="row.itemIds" multiple placeholder="选择子项（可多选）" size="small"
                                style="width: 100%" :disabled="!row.groupId" @change="handleCfgRowItemsChange(row)">
                                <el-option v-for="it in itemOptionsCache[row.groupId] || []" :key="it.id"
                                    :value="it.id" :label="it.itemName || it.name || it.code"
                                    :disabled="isCfgItemDisabled(it.id, row) || isCfgItemExisting(row.groupId, it.id)" />
                            </el-select>
                        </el-form-item>
                        <!-- 选中的子项逐条填写各自数量 -->
                        <div v-if="row.items.length" class="cfg-subitem-list">
                            <div class="cfg-subitem cfg-subitem-header">
                                <span class="cfg-subitem-name">子项</span>
                                <span>默认数量</span>
                                <span>最小数量</span>
                                <span>最大数量</span>
                            </div>
                            <div v-for="sub in row.items" :key="sub.itemId" class="cfg-subitem">
                                <span class="cfg-subitem-name" :title="cfgRowItemName(row, sub.itemId)">
                                    {{ cfgRowItemName(row, sub.itemId) }}
                                </span>
                                <el-input-number v-model="sub.defaultQty" :min="0" :max="9999" size="small"
                                    controls-position="right" />
                                <el-input-number v-model="sub.minQty" :min="0" :max="9999" size="small"
                                    controls-position="right" />
                                <el-input-number v-model="sub.maxQty" :min="0" :max="9999" size="small"
                                    controls-position="right" />
                            </div>
                        </div>
                    </div>
                    <el-button type="primary" size="small" icon="el-icon-plus" class="cfg-card-add"
                        @click="addCfgRow">添加配置项</el-button>
                    <el-form-item label="状态" style="margin-top: 16px">
                        <el-switch v-model="cfgForm.status" :active-value="1" :inactive-value="0"
                            active-text="激活" inactive-text="停用" />
                    </el-form-item>
                </template>

                <!-- 编辑：单条记录 -->
                <template v-else>
                    <el-form-item label="配置分组" prop="groupId">
                        <el-select v-model="cfgForm.groupId" placeholder="请选择分组" filterable style="width: 100%"
                            @change="handleCfgGroupChange">
                            <el-option v-for="g in groupOptions" :key="g.id" :value="g.id"
                                :label="g.name || g.code" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="配置子项" prop="itemId">
                        <el-select v-model="cfgForm.itemId" placeholder="请先选择分组" filterable style="width: 100%"
                            :disabled="!cfgForm.groupId">
                            <el-option v-for="it in itemOptionsCache[cfgForm.groupId] || []" :key="it.id"
                                :value="it.id" :label="it.itemName || it.name || it.code" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="数量设置">
                        <div class="cfg-qty-row">
                            <el-form-item label="默认数量" prop="defaultQty" label-width="70px">
                                <el-input-number v-model="cfgForm.defaultQty" :min="0" :max="9999"
                                    controls-position="right" style="width: 90px" />
                            </el-form-item>
                            <el-form-item label="最小数量" prop="minQty" label-width="70px">
                                <el-input-number v-model="cfgForm.minQty" :min="0" :max="9999"
                                    controls-position="right" style="width: 90px" />
                            </el-form-item>
                            <el-form-item label="最大数量" prop="maxQty" label-width="70px">
                                <el-input-number v-model="cfgForm.maxQty" :min="0" :max="9999"
                                    controls-position="right" style="width: 90px" />
                            </el-form-item>
                        </div>
                    </el-form-item>
                    <el-form-item label="分组排序" prop="groupOrder">
                        <el-input-number v-model="cfgForm.groupOrder" :min="0" :max="9999" controls-position="right" />
                    </el-form-item>
                    <el-form-item label="子项排序" prop="itemOrder">
                        <el-input-number v-model="cfgForm.itemOrder" :min="0" :max="9999" controls-position="right" />
                    </el-form-item>
                    <el-form-item label="状态" prop="status">
                        <el-switch v-model="cfgForm.status" :active-value="1" :inactive-value="0"
                            active-text="激活" inactive-text="停用" />
                    </el-form-item>
                </template>
            </el-form>
            <div slot="footer" style="display: flex; justify-content: flex-end; gap: 6px">
                <el-button @click="cfgVisible = false">取消</el-button>
                <el-button type="primary" :loading="cfgSaving" @click="handleCfgSubmit">保存</el-button>
            </div>
        </el-dialog>

        <!-- 新增/编辑模板弹窗 -->
        <el-dialog :title="tplEditId ? '编辑模板' : '新增模板'" :visible.sync="tplVisible" width="520px"
            :close-on-click-modal="false" @closed="resetTplForm">
            <el-form ref="tplFormRef" :model="tplForm" :rules="tplRules" label-width="110px">
                <el-form-item label="模板名称" prop="templateName">
                    <el-input v-model="tplForm.templateName" placeholder="请输入模板名称" maxlength="50"
                        show-word-limit clearable />
                </el-form-item>
                <el-form-item label="关联产品型号" prop="modelId">
                    <el-select v-model="tplForm.modelId" placeholder="请选择产品型号" style="width: 100%"
                        @visible-change="visible => visible && fetchModelOptions()">
                        <el-option v-if="modelLoading" :key="'loading'" value="" label="加载中..." disabled />
                        <el-option v-for="item in modelOptions" :key="item.id" :value="item.id" :label="item.name">
                            <div style="display: flex; align-items: center; justify-content: space-between">
                                <span>{{ item.name }}</span>
                                <span style="color: #9ca3af; font-size: 12px">{{ item.code }}</span>
                            </div>
                        </el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="备注" prop="remark">
                    <el-input v-model="tplForm.remark" type="textarea" :rows="2" placeholder="请输入备注"
                        maxlength="200" show-word-limit />
                </el-form-item>
                <el-form-item label="排序" prop="sortOrder">
                    <el-input-number v-model="tplForm.sortOrder" :min="0" :max="9999" controls-position="right" />
                </el-form-item>
                <el-form-item label="状态" prop="status">
                    <el-switch v-model="tplForm.status" :active-value="1" :inactive-value="0"
                        active-text="激活" inactive-text="停用" />
                </el-form-item>
            </el-form>
            <div slot="footer" style="display: flex; justify-content: flex-end; gap: 6px">
                <el-button @click="tplVisible = false">取消</el-button>
                <el-button type="primary" :loading="tplLoading" @click="handleTemplateSubmit">保存</el-button>
            </div>
        </el-dialog>
    </el-card>
</template>

<script>
import {
    templateProductSizeApi,
    templateProductModelApi,
    templateProductItemApi,
    productSizeList,
    productModelList,
    configGroupList,
    configItemList
} from '@/api/quote'

export default {
    name: 'TemplateProductSize',
    data() {
        return {
            activeTemplate: '',
            activeTab: 'size', // 右侧标签页：size 产品尺寸 / config 产品配置
            expandedKeys: [], // 左侧已展开的产品类型分组 key
            sizeLoading: false, // 正在加载中
            currentPage: 1,
            pageSize: 10,
            currentTotal: 0,
            tableData: [], // 当前页的尺寸行
            templatePage: 1, // 左侧模板列表当前页
            templateSize: 10, // 左侧模板列表每页条数
            templateTotal: 0, // 左侧模板列表总条数
            templateLoading: false, // 左侧模板列表加载中
            templateList: [], // 左侧模板产品列表（滚动累积）
            templateData: {}, // templateId -> 模板记录（名称关联）
            sizeMap: {}, // sizeId -> 产品尺寸（名称/规格/价格关联）
            sizeOptions: [], // 新增弹窗尺寸选项（与 sizeMap 同源）
            sizeModelId: null, // sizeOptions/sizeMap 当前对应的产品型号 id
            // 产品配置列表
            cfgLoading: false,
            cfgData: [],
            cfgPage: 1,
            cfgSize: 10,
            cfgTotal: 0,
            cfgGroupFilter: null, // 产品配置列表：按配置分组筛选
            existingCfgKeys: {}, // 当前模板已存在的配置：groupId_itemId -> true（用于新增去重）
            groupMap: {}, // 配置分组字典：groupId -> 分组记录
            itemMap: {}, // 配置子项字典：itemId -> 子项记录
            // 新增/编辑尺寸弹窗
            addVisible: false,
            addLoading: false,
            editId: null, // 有值表示编辑模式
            addForm: {
                sizeId: null, // 编辑时用（单选）
                sizeIds: [], // 新增时用（多选批量添加）
                remark: '',
                sortOrder: 0,
                status: 1,
            },
            addRules: {
                sizeId: [{ required: true, message: '请选择尺寸', trigger: 'change' }],
                sizeIds: [{ required: true, type: 'array', min: 1, message: '请选择尺寸', trigger: 'change' }],
            },
            // 新增/编辑配置弹窗
            cfgVisible: false,
            cfgSaving: false,
            cfgEditId: null, // 有值表示编辑模式
            groupOptions: [],
            itemOptionsCache: {}, // 配置子项选项缓存：groupId -> items
            cfgRows: [], // 新增模式的配置动态行（一次可添加多条）
            cfgForm: {
                groupId: null,
                itemId: null,
                defaultQty: 0,
                minQty: 0,
                maxQty: 3,
                groupOrder: 0,
                itemOrder: 0,
                status: 1,
            },
            cfgRules: {
                groupId: [{ required: true, message: '请选择分组', trigger: 'change' }],
                itemId: [{ required: true, message: '请选择子项', trigger: 'change' }],
            },
            // 新增/编辑模板弹窗
            tplVisible: false,
            tplLoading: false,
            tplEditId: null, // 有值表示编辑模式
            tplDetail: null, // 编辑时缓存的模板详情（提交时原样带回尺寸/配置，避免被清空）
            modelOptions: [],
            modelLoading: false,
            tplForm: {
                templateName: '',
                modelId: null,
                remark: '',
                sortOrder: 0,
                status: 1,
            },
            tplRules: {
                templateName: [{ required: true, message: '请输入模板名称', trigger: 'blur' }],
                modelId: [{ required: true, message: '请选择产品型号', trigger: 'change' }],
            },
        }
    },
    computed: {
        // 左侧分组：按模板关联的产品型号分组，未关联的归入“未关联型号”
        typeGroups() {
            const groups = []
            const index = {}
            this.templateList.forEach(tpl => {
                const model = tpl.productModel || null
                const key = model ? String(model.id) : 'none'
                if (!index[key]) {
                    index[key] = {
                        key,
                        name: model ? model.name : '未关联型号',
                        modelId: model ? model.id : null,
                        templates: [],
                    }
                    groups.push(index[key])
                }
                index[key].templates.push(tpl)
            })
            return groups
        },
        activeTemplateName() {
            const template = this.templateData[this.activeTemplate]
            return template ? template.templateName : ''
        },
        // 当前选中模板关联的产品型号 id（用于过滤尺寸选项）
        activeModelId() {
            const template = this.templateData[this.activeTemplate]
            if (!template) return null
            return template.modelId || (template.productModel ? template.productModel.id : null)
        },
        hasMoreTemplates() {
            return this.templateList.length < this.templateTotal
        },
    },
    mounted() {
        this.fetchTemplateList();
    },
    methods: {
        // 金额展示：千分位
        formatPrice(val) {
            if (val === null || val === undefined || val === '') return '--'
            return Number(val).toLocaleString()
        },

        // 取行对应的尺寸信息：优先接口回填的 productSize，否则查前端字典
        sizeOf(row) {
            return row.productSize || this.sizeMap[row.sizeId] || null
        },

        /* ------------------------------ 左侧产品类型树 ------------------------------ */
        groupKeyOf(tpl) {
            return tpl.productModel ? String(tpl.productModel.id) : 'none'
        },

        isGroupExpanded(key) {
            return this.expandedKeys.indexOf(key) !== -1
        },

        toggleGroup(key) {
            const i = this.expandedKeys.indexOf(key)
            if (i === -1) {
                this.expandedKeys.push(key)
            } else {
                this.expandedKeys.splice(i, 1)
            }
        },

        ensureExpanded(key) {
            if (!this.isGroupExpanded(key)) this.expandedKeys.push(key)
        },

        // 获取模板产品列表（左侧菜单，滚动分页，append 时追加下一页）
        async fetchTemplateList(append = false) {
            if (this.templateLoading) return
            this.templateLoading = true
            try {
                const param = {
                    current: this.templatePage,
                    size: this.templateSize,
                    filter: [],
                    sorted: [{ name: 'sortOrder', type: 'ASC' }],
                }
                const res = await templateProductModelApi.page(param)
                if (res && res.code === '0000') {
                    const records = res.result.records || []
                    this.templateList = append ? this.templateList.concat(records) : records
                    records.forEach(item => {
                        this.$set(this.templateData, item.id, item)
                    })
                    this.templateTotal = res.result.total || 0
                    if (!append) this.afterTemplateListRefresh()
                }
            } catch (error) {
                console.error('获取模板产品列表失败：', error)
            } finally {
                this.templateLoading = false
            }
        },

        // 列表刷新后：维护选中模板（默认选中第一个）并展开其所在分组
        afterTemplateListRefresh() {
            if (!this.templateList.length) {
                this.activeTemplate = ''
                this.tableData = []
                this.currentTotal = 0
                this.cfgData = []
                this.cfgTotal = 0
                return
            }
            const current = this.templateList.find(t => String(t.id) === String(this.activeTemplate))
            const target = current || this.templateList[0]
            this.ensureExpanded(this.groupKeyOf(target))
            if (!current) {
                this.handleTemplateSelect(target.id)
            }
        },

        // 左侧列表滚动到底部时加载下一页
        handleTemplateScroll(event) {
            const el = event.target
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                this.loadMoreTemplates()
            }
        },

        // 加载下一页（追加到模板列表尾部）
        loadMoreTemplates() {
            if (this.templateLoading || !this.hasMoreTemplates) return
            this.templatePage += 1
            this.fetchTemplateList(true)
        },

        // 重新加载左侧模板列表（回到第一页，模板增删改后调用）
        refreshTemplates() {
            this.templatePage = 1
            this.fetchTemplateList(false)
        },

        // 选择模板：从第一页开始加载该模板的尺寸/配置
        handleTemplateSelect(templateId) {
            this.activeTemplate = templateId
            this.currentPage = 1
            this.fetchSizes()
            this.fetchSizeMap()
            this.cfgPage = 1
            this.cfgGroupFilter = null
            if (this.activeTab === 'config') this.loadConfigTab()
        },

        // 右侧标签页切换
        switchTab(tab) {
            this.activeTab = tab
            if (tab === 'config' && this.activeTemplate) this.loadConfigTab()
        },

        // 跳转产品型号页（新建设备类型）
        goProductModel() {
            this.$router.push('/productModel')
        },

        /* ------------------------------ 模板增删改 ------------------------------ */
        // 获取产品型号下拉选项
        async fetchModelOptions() {
            if (this.modelOptions.length) return // 已加载过则复用
            this.modelLoading = true
            try {
                const param = {
                    filter: [],
                    sorted: [],
                    current: 0,
                    size: 100,
                }
                const res = await productModelList(param)
                if (res && res.code === '0000') {
                    this.modelOptions = res.result.records || []
                }
            } catch (error) {
                console.error('获取产品型号选项失败：', error)
            } finally {
                this.modelLoading = false
            }
        },

        // 新增模板：在当前分组下新建，默认选中该分组对应的产品型号
        openTemplateAddDialog(group) {
            this.tplEditId = null
            this.tplDetail = null
            this.tplForm = {
                templateName: '',
                modelId: group ? group.modelId : null,
                remark: '',
                sortOrder: 0,
                status: 1,
            }
            this.tplVisible = true
            this.fetchModelOptions()
        },

        // 编辑模板：先拉详情回填（提交时原样带回尺寸/配置，避免被清空）
        async openTemplateEditDialog(template) {
            try {
                const res = await templateProductModelApi.detail(template.id)
                if (!res || res.code !== '0000') return
                const detail = res.result || {}
                this.tplEditId = detail.id
                this.tplDetail = detail
                this.tplForm = {
                    templateName: detail.templateName || '',
                    modelId: detail.modelId || (detail.productModel ? detail.productModel.id : null),
                    remark: detail.remark || '',
                    sortOrder: detail.sortOrder || 0,
                    status: detail.status !== undefined && detail.status !== null ? detail.status : 1,
                }
                this.tplVisible = true
                this.fetchModelOptions()
            } catch (error) {
                console.error('获取模板详情失败：', error)
            }
        },

        // 重置模板表单
        resetTplForm() {
            this.tplEditId = null
            this.tplDetail = null
            this.tplForm = {
                templateName: '',
                modelId: null,
                remark: '',
                sortOrder: 0,
                status: 1,
            }
            this.$refs.tplFormRef && this.$refs.tplFormRef.clearValidate()
        },

        // 提交保存模板（新增/编辑）
        handleTemplateSubmit() {
            this.$refs.tplFormRef.validate(async (valid) => {
                if (!valid) return
                this.tplLoading = true
                try {
                    const param = {
                        templateName: this.tplForm.templateName,
                        modelId: this.tplForm.modelId,
                        remark: this.tplForm.remark,
                        sortOrder: this.tplForm.sortOrder,
                        status: this.tplForm.status,
                        templateProductSizeList: [],
                        templateProductItemList: [],
                    }
                    if (this.tplEditId && this.tplDetail) {
                        // 原样带回已有尺寸行
                        param.templateProductSizeList = this.tplDetail.templateProductSizeList || []
                        // 拍平各分组下的配置项（保留子行 id，避免更新时被清空）
                        const rows = []
                        ;(this.tplDetail.templateProductGroupList || []).forEach(group => {
                            ;(group.templateProductItemList || []).forEach(item => {
                                rows.push({
                                    id: item.id,
                                    templateId: item.templateId,
                                    status: item.status,
                                    groupId: item.groupId || group.groupId,
                                    itemId: item.itemId !== undefined && item.itemId !== null
                                        ? item.itemId
                                        : (item.configItem ? item.configItem.id : item.id),
                                    defaultQty: item.defaultQty !== undefined && item.defaultQty !== null ? item.defaultQty : 1,
                                    minQty: item.minQty !== undefined && item.minQty !== null ? item.minQty : 1,
                                    maxQty: item.maxQty !== undefined && item.maxQty !== null ? item.maxQty : 3,
                                    groupOrder: item.groupOrder || 0,
                                    itemOrder: item.itemOrder || 0,
                                })
                            })
                        })
                        param.templateProductItemList = rows
                    }
                    const res = this.tplEditId
                        ? await templateProductModelApi.update({ id: this.tplEditId, ...param })
                        : await templateProductModelApi.add(param)
                    if (res && res.code === '0000') {
                        this.$message.success(this.tplEditId ? '更新成功' : '新增成功')
                        this.tplVisible = false
                        this.refreshTemplates()
                    } else {
                        this.$message.error(res.message || (this.tplEditId ? '更新失败' : '新增失败'))
                    }
                } catch (error) {
                    console.error('保存模板失败：', error)
                } finally {
                    this.tplLoading = false
                }
            })
        },

        // 删除模板
        handleTemplateDelete(template) {
            this.$confirm(`此操作将永久删除模板【${template.templateName}】, 是否继续?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await templateProductModelApi.delete(template.id)
                    if (res && res.code === '0000') {
                        this.$message.success('删除成功')
                        this.refreshTemplates()
                    } else {
                        this.$message.error(res.message || '删除失败')
                    }
                } catch (error) {
                    console.error('删除模板失败：', error)
                }
            }).catch(() => {})
        },

        /* ------------------------------ 产品尺寸 ------------------------------ */
        // 分页拉取当前选中模板的尺寸（默认每页 10 条）
        async fetchSizes() {
            if (!this.activeTemplate || this.sizeLoading) return
            this.sizeLoading = true
            try {
                const param = {
                    current: this.currentPage,
                    size: this.pageSize,
                    filter: [
                        {
                            name: 'templateId',
                            type: 'EQ',
                            value: this.activeTemplate
                        }
                    ],
                    sorted: [{ name: 'sortOrder', type: 'ASC' }],
                }
                const res = await templateProductSizeApi.page(param)
                if (res && res.code === '0000') {
                    this.tableData = res.result.records || []
                    this.currentTotal = res.result.total || 0
                }
            } catch (error) {
                console.error('获取模板产品尺寸失败：', error)
                this.tableData = []
            } finally {
                this.sizeLoading = false
            }
        },

        // 每页条数变化：回到第一页重新加载
        handleSizeChange(val) {
            this.pageSize = val
            this.currentPage = 1
            this.fetchSizes()
        },

        // 翻页
        handleCurrentChange(page) {
            this.currentPage = page
            this.fetchSizes()
        },

        // 加载尺寸字典（同时作为新增弹窗的尺寸选项），按当前模板的产品型号过滤
        async fetchSizeMap() {
            const modelId = this.activeModelId
            if (this.sizeOptions.length && this.sizeModelId === modelId) return // 同型号已加载过则复用
            try {
                const filter = []
                if (modelId) {
                    filter.push({ name: 'modelId', type: 'EQ', value: modelId })
                }
                const param = {
                    filter,
                    sorted: [],
                    current: 1,
                    size: 500,
                }
                const res = await productSizeList(param)
                if (res && res.code === '0000') {
                    const records = res.result.records || []
                    this.sizeOptions = records
                    const map = {}
                    records.forEach(item => {
                        map[item.id] = item
                    })
                    this.sizeMap = map
                    this.sizeModelId = modelId
                }
            } catch (error) {
                console.error('获取尺寸字典失败：', error)
            }
        },

        // 打开新增弹窗（模板取当前选中的左侧模板）
        openAddDialog() {
            this.editId = null
            this.addForm = {
                sizeId: null,
                sizeIds: [],
                remark: '',
                sortOrder: 0,
                status: 1,
            }
            this.addVisible = true
            this.fetchSizeMap()
        },

        // 打开编辑弹窗：行数据回填
        openEditDialog(row) {
            this.editId = row.id
            this.addForm = {
                sizeId: row.sizeId || null,
                sizeIds: [],
                remark: row.remark || '',
                sortOrder: row.sortOrder || 0,
                status: row.status !== undefined && row.status !== null ? row.status : 1,
            }
            this.addVisible = true
            this.fetchSizeMap()
        },

        // 重置弹窗表单
        resetAddForm() {
            this.editId = null
            this.addForm = {
                sizeId: null,
                sizeIds: [],
                remark: '',
                sortOrder: 0,
                status: 1,
            }
            this.$refs.addFormRef && this.$refs.addFormRef.clearValidate()
        },

        // 下拉工具条：全选所有尺寸
        selectAllSizes() {
            this.addForm.sizeIds = this.sizeOptions.map(item => item.id)
            this.closeSizeSelect()
        },

        // 下拉工具条：清空所有已选尺寸
        clearAllSizes() {
            this.addForm.sizeIds = []
            this.closeSizeSelect()
        },

        // 关闭尺寸下拉选择器
        closeSizeSelect() {
            this.$nextTick(() => {
                if (this.$refs.sizeSelectRef) {
                    this.$refs.sizeSelectRef.visible = false
                }
            })
        },

        // 提交保存（编辑单条更新；新增支持多选尺寸，逐条批量提交）
        handleSaveSubmit() {
            this.$refs.addFormRef.validate(async (valid) => {
                if (!valid) return
                this.addLoading = true
                try {
                    if (this.editId) {
                        const param = {
                            templateId: this.activeTemplate,
                            sizeId: this.addForm.sizeId,
                            remark: this.addForm.remark,
                            sortOrder: this.addForm.sortOrder,
                            status: this.addForm.status,
                        }
                        const res = await templateProductSizeApi.update({ id: this.editId, ...param })
                        if (res && res.code === '0000') {
                            this.$message.success('更新成功')
                            this.addVisible = false
                            this.fetchSizes()
                        } else {
                            this.$message.error(res.message || '更新失败')
                        }
                    } else {
                        // 新增：多选尺寸走批量保存接口
                        const param = this.addForm.sizeIds.map((sizeId, index) => ({
                            templateId: this.activeTemplate,
                            sizeId,
                            remark: this.addForm.remark,
                            sortOrder: this.addForm.sortOrder + index,
                            status: this.addForm.status,
                        }))
                        const res = await templateProductSizeApi.batchSave(param)
                        if (res && res.code === '0000') {
                            this.$message.success(`新增成功，共 ${param.length} 条`)
                            this.addVisible = false
                            this.fetchSizes()
                        } else {
                            this.$message.error(res.message || '新增失败')
                        }
                    }
                } catch (error) {
                    console.error('保存模板产品尺寸失败：', error)
                } finally {
                    this.addLoading = false
                }
            })
        },

        // 删除尺寸
        handleDelete(row) {
            const size = this.sizeOf(row)
            const name = (size && size.name) || `ID ${row.id}`
            this.$confirm(`此操作将永久删除尺寸配置【${name}】, 是否继续?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await templateProductSizeApi.delete(row.id)
                    if (res && res.code === '0000') {
                        this.$message.success('删除成功')
                        this.fetchSizes()
                    } else {
                        this.$message.error(res.message || '删除失败')
                    }
                } catch (error) {
                    console.error('删除模板产品尺寸失败：', error)
                }
            }).catch(() => {})
        },

        /* ------------------------------ 产品配置 ------------------------------ */
        // 进入产品配置标签：先保证分组/子项字典已加载，再拉列表
        loadConfigTab() {
            this.fetchGroupMap()
            this.fetchItemMap()
            this.fetchConfigs()
        },

        // 分页拉取当前选中模板的产品配置
        async fetchConfigs() {
            if (!this.activeTemplate || this.cfgLoading) return
            this.cfgLoading = true
            try {
                const filter = [
                    {
                        name: 'templateId',
                        type: 'EQ',
                        value: this.activeTemplate
                    }
                ]
                if (this.cfgGroupFilter) {
                    filter.push({
                        name: 'groupId',
                        type: 'EQ',
                        value: this.cfgGroupFilter
                    })
                }
                const param = {
                    current: this.cfgPage,
                    size: this.cfgSize,
                    filter,
                    sorted: [
                        { name: 'groupOrder', type: 'ASC' },
                        { name: 'itemOrder', type: 'ASC' },
                    ],
                }
                const res = await templateProductItemApi.page(param)
                if (res && res.code === '0000') {
                    this.cfgData = res.result.records || []
                    this.cfgTotal = res.result.total || 0
                }
            } catch (error) {
                console.error('获取模板产品配置失败：', error)
                this.cfgData = []
            } finally {
                this.cfgLoading = false
            }
        },

        // 配置分组筛选变化：回到第一页重新加载
        handleCfgGroupFilterChange() {
            this.cfgPage = 1
            this.fetchConfigs()
        },

        // 配置列表：每页条数变化
        handleCfgSizeChange(val) {
            this.cfgSize = val
            this.cfgPage = 1
            this.fetchConfigs()
        },

        // 配置列表：翻页
        handleCfgCurrentChange(page) {
            this.cfgPage = page
            this.fetchConfigs()
        },

        // 加载配置分组字典
        async fetchGroupMap() {
            if (Object.keys(this.groupMap).length) return // 已加载过则复用
            try {
                const param = {
                    filter: [],
                    sorted: [],
                    current: 0,
                    size: 500,
                }
                const res = await configGroupList(param)
                if (res && res.code === '0000') {
                    const map = {};
                    (res.result.records || []).forEach(item => {
                        map[item.id] = item
                    })
                    this.groupMap = map
                    this.groupOptions = res.result.records || []
                }
            } catch (error) {
                console.error('获取配置分组字典失败：', error)
            }
        },

        // 加载配置子项字典
        async fetchItemMap() {
            if (Object.keys(this.itemMap).length) return // 已加载过则复用
            try {
                const param = {
                    filter: [],
                    sorted: [],
                    current: 0,
                    size: 500,
                }
                const res = await configItemList(param)
                if (res && res.code === '0000') {
                    const map = {};
                    (res.result.records || []).forEach(item => {
                        map[item.id] = item
                    })
                    this.itemMap = map
                }
            } catch (error) {
                console.error('获取配置子项字典失败：', error)
            }
        },

        // 打开新增配置弹窗（模板取当前选中的左侧模板，默认一行空配置项）
        openCfgAddDialog() {
            this.cfgEditId = null
            this.cfgForm = {
                groupId: null,
                itemId: null,
                defaultQty: 0,
                minQty: 0,
                maxQty: 3,
                groupOrder: 0,
                itemOrder: 0,
                status: 1,
            }
            this.cfgRows = [this.newCfgRow()]
            this.existingCfgKeys = {}
            this.cfgVisible = true
            this.fetchGroupMap()
            this.fetchExistingCfgItems()
        },

        // 新增一条空配置行
        newCfgRow() {
            return {
                groupId: null,
                itemIds: [], // 多选的子项 id
                items: [], // 选中子项对应的数量行：{ itemId, defaultQty, minQty, maxQty }
                groupOrder: 0,
                itemOrder: 0,
            }
        },

        // 添加配置项行
        addCfgRow() {
            this.cfgRows.push(this.newCfgRow())
        },

        // 删除配置项行
        removeCfgRow(index) {
            this.cfgRows.splice(index, 1)
        },

        // 配置行：切换分组，清空子项并按分组加载子项选项
        handleCfgRowGroupChange(row) {
            row.itemIds = []
            row.items = []
            this.fetchCfgItemOptions(row.groupId)
        },

        // 配置行：多选子项变化后，同步出每个子项的数量行（保留已填数量）
        handleCfgRowItemsChange(row) {
            const existing = row.items || []
            row.items = row.itemIds.map(itemId => {
                const old = existing.find(it => it.itemId === itemId)
                return old || { itemId, defaultQty: 0, minQty: 0, maxQty: 3 }
            })
        },

        // 配置行：子项名称展示
        cfgRowItemName(row, itemId) {
            const options = this.itemOptionsCache[row.groupId] || []
            const item = options.find(it => it.id === itemId)
            return item ? (item.itemName || item.name || item.code) : itemId
        },

        // 配置行：分组已被其他卡片选中时禁用，避免重复
        isCfgGroupDisabled(groupId, currentRow) {
            return this.cfgRows.some(row => row !== currentRow && row.groupId === groupId)
        },

        // 配置行：同组子项已被其他卡片选中时禁用，避免重复
        isCfgItemDisabled(itemId, currentRow) {
            return this.cfgRows.some(row =>
                row !== currentRow && row.groupId === currentRow.groupId && row.itemIds.includes(itemId)
            )
        },

        // 拉取当前模板已保存的配置，用于新增时按“分组+子项”去重
        async fetchExistingCfgItems() {
            if (!this.activeTemplate) return
            try {
                const param = {
                    current: 1,
                    size: 1000,
                    filter: [{ name: 'templateId', type: 'EQ', value: this.activeTemplate }],
                    sorted: [],
                }
                const res = await templateProductItemApi.page(param)
                if (res && res.code === '0000') {
                    const map = {}
                    ;(res.result.records || []).forEach(item => {
                        map[`${item.groupId}_${item.itemId}`] = true
                    })
                    this.existingCfgKeys = map
                }
            } catch (error) {
                console.error('获取已存在模板配置失败：', error)
            }
        },

        // 当前模板下该分组+子项是否已存在
        isCfgItemExisting(groupId, itemId) {
            return !!(groupId && itemId && this.existingCfgKeys[`${groupId}_${itemId}`])
        },

        // 打开编辑配置弹窗：行数据回填
        openCfgEditDialog(row) {
            this.cfgEditId = row.id
            this.cfgForm = {
                groupId: row.groupId,
                itemId: row.itemId,
                defaultQty: row.defaultQty,
                minQty: row.minQty,
                maxQty: row.maxQty,
                groupOrder: row.groupOrder,
                itemOrder: row.itemOrder,
                status: row.status,
            }
            this.cfgVisible = true
            this.fetchGroupMap()
            this.fetchCfgItemOptions(row.groupId)
        },

        // 切换分组：清空子项并按分组加载子项（带缓存）
        handleCfgGroupChange(groupId) {
            this.cfgForm.itemId = null
            this.fetchCfgItemOptions(groupId)
        },

        // 按分组加载配置子项选项
        async fetchCfgItemOptions(groupId) {
            if (!groupId || this.itemOptionsCache[groupId]) return
            try {
                const param = {
                    filter: [{ name: 'groupId', type: 'EQ', value: groupId }],
                    sorted: [],
                    current: 0,
                    size: 200,
                }
                const res = await configItemList(param)
                if (res && res.code === '0000') {
                    this.$set(this.itemOptionsCache, groupId, res.result.records || [])
                }
            } catch (error) {
                console.error('获取配置子项选项失败：', error)
            }
        },

        // 重置配置表单
        resetCfgForm() {
            this.cfgEditId = null
            this.cfgRows = []
            this.cfgForm = {
                groupId: null,
                itemId: null,
                defaultQty: 0,
                minQty: 0,
                maxQty: 3,
                groupOrder: 0,
                itemOrder: 0,
                status: 1,
            }
            this.$refs.cfgFormRef && this.$refs.cfgFormRef.clearValidate()
        },

        // 提交保存配置（编辑单条更新；新增校验动态行后逐条批量提交）
        handleCfgSubmit() {
            // 新增模式：先校验动态行
            if (!this.cfgEditId) {
                if (!this.cfgRows.length) {
                    this.$message.warning('请添加至少一条配置项')
                    return
                }
                const groupIds = this.cfgRows.map(row => row.groupId).filter(Boolean)
                if (new Set(groupIds).size !== groupIds.length) {
                    this.$message.warning('配置分组不能重复选择')
                    return
                }
                for (const [index, row] of this.cfgRows.entries()) {
                    if (!row.groupId || !row.itemIds.length) {
                        this.$message.warning('请完整选择每个配置项的分组和子项')
                        return
                    }
                    for (const sub of row.items) {
                        const name = this.cfgRowItemName(row, sub.itemId)
                        if (sub.defaultQty < sub.minQty) {
                            this.$message.warning(`配置分组 ${index + 1}【${name}】：默认数量不能小于最小数量`)
                            return
                        }
                        if (sub.defaultQty > sub.maxQty) {
                            this.$message.warning(`配置分组 ${index + 1}【${name}】：默认数量不能大于最大数量`)
                            return
                        }
                    }
                }
            }
            this.$refs.cfgFormRef.validate(async (valid) => {
                if (!valid) return
                this.cfgSaving = true
                try {
                    if (this.cfgEditId) {
                        const param = {
                            templateId: this.activeTemplate,
                            ...this.cfgForm,
                        }
                        const res = await templateProductItemApi.update({ id: this.cfgEditId, ...param })
                        if (res && res.code === '0000') {
                            this.$message.success('更新成功')
                            this.cfgVisible = false
                            this.fetchConfigs()
                        } else {
                            this.$message.error(res.message || '更新失败')
                        }
                    } else {
                        // 提交前再按当前模板已保存的配置去重，避免重复新增同一分组+子项
                        await this.fetchExistingCfgItems()
                        for (const row of this.cfgRows) {
                            for (const sub of row.items) {
                                if (this.isCfgItemExisting(row.groupId, sub.itemId)) {
                                    const name = this.cfgRowItemName(row, sub.itemId)
                                    this.$message.warning(`子项【${name}】在当前模板下已存在，不能重复新增`)
                                    return
                                }
                            }
                        }
                        // 每行按选中的多个子项展开成多条配置，走批量保存接口
                        const param = []
                        this.cfgRows.forEach(row => {
                            row.items.forEach((sub, itemIndex) => {
                                param.push({
                                    templateId: this.activeTemplate,
                                    groupId: row.groupId,
                                    itemId: sub.itemId,
                                    defaultQty: sub.defaultQty,
                                    minQty: sub.minQty,
                                    maxQty: sub.maxQty,
                                    groupOrder: row.groupOrder,
                                    itemOrder: row.itemOrder + itemIndex,
                                    status: this.cfgForm.status,
                                })
                            })
                        })
                        const res = await templateProductItemApi.batchSave(param)
                        if (res && res.code === '0000') {
                            this.$message.success(`新增成功，共 ${param.length} 条`)
                            this.cfgVisible = false
                            this.fetchConfigs()
                        } else {
                            this.$message.error(res.message || '新增失败')
                        }
                    }
                } catch (error) {
                    console.error('保存模板产品配置失败：', error)
                } finally {
                    this.cfgSaving = false
                }
            })
        },

        // 删除配置
        handleCfgDelete(row) {
            const item = this.itemMap[row.itemId]
            const name = (item && (item.itemName || item.name)) || `ID ${row.id}`
            this.$confirm(`此操作将永久删除配置【${name}】, 是否继续?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await templateProductItemApi.delete(row.id)
                    if (res && res.code === '0000') {
                        this.$message.success('删除成功')
                        // 删除后如果当前页已空且不是第一页，回退一页
                        if (this.cfgData.length === 1 && this.cfgPage > 1) {
                            this.cfgPage -= 1
                        }
                        this.fetchConfigs()
                    } else {
                        this.$message.error(res.message || '删除失败')
                    }
                } catch (error) {
                    console.error('删除模板产品配置失败：', error)
                }
            }).catch(() => {})
        },
    }
}
</script>

<style scoped>
.product-size-container {
    display: flex;
    min-height: 560px;
    flex-wrap: nowrap;
}

/* 左侧产品类型面板 */
.type-panel {
    width: 250px;
    min-width: 250px;
    flex-shrink: 0;
    border-right: 1px solid #ebeef5;
    display: flex;
    flex-direction: column;
}

.type-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 12px;
}

.type-panel-title {
    font-size: 15px;
    font-weight: bold;
    color: #303133;
}

.new-type-btn {
    padding: 0;
    font-size: 13px;
}

/* 左侧模板列表滚动容器：滚动到底部时加载下一页 */
.template-scroll {
    flex: 1;
    max-height: 560px;
    overflow-y: auto;
}

.type-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
}

.type-group-name {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.type-group-header i {
    font-size: 12px;
    color: #909399;
}

.template-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px 8px 24px;
    cursor: pointer;
}

.template-row:hover {
    background-color: #f5f7fa;
}

.template-row.active .template-name {
    color: #1890ff;
}

.template-name {
    font-size: 13px;
    color: #606266;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.template-actions {
    flex-shrink: 0;
}

.template-actions .el-button {
    padding: 0;
    margin-left: 8px;
    font-size: 12px;
}

.tpl-del {
    color: #f56c6c;
}

.tpl-edit {
    color: #1890ff;
}

.template-add {
    padding: 8px 12px 8px 40px;
    font-size: 13px;
    color: #909399;
    cursor: pointer;
}

.template-add:hover {
    color: #1890ff;
}

.load-more {
    padding: 10px 0;
    text-align: center;
    font-size: 12px;
    color: #909399;
}

/* 右侧模板配置面板 */
.config-panel {
    flex: 1;
    min-width: 0;
    padding-left: 24px;
    overflow-x: auto;
}

.config-title {
    font-size: 15px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 12px;
}

.config-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
}

.config-tab {
    padding: 5px 14px;
    font-size: 13px;
    color: #606266;
    cursor: pointer;
    border-radius: 4px;
    user-select: none;
}

.config-tab.active {
    color: #1890ff;
    background-color: rgba(24, 144, 255, 0.08);
    font-weight: 500;
}

.pane-toolbar {
    margin-bottom: 12px;
}

.cfg-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
}

/* 编辑配置弹窗：数量三项同一行 */
.cfg-qty-row {
    display: flex;
    gap: 10px;
}

.cfg-qty-row .el-form-item {
    margin-bottom: 0;
}

/* 新增配置卡片组 */
.cfg-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    background-color: #fafafa;
}

.cfg-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    font-weight: bold;
    color: #303133;
}

.cfg-card-del {
    color: #f56c6c;
}

.cfg-card-add {
    width: 100%;
}

/* 卡片内：选中子项逐条填数量 */
.cfg-subitem-list {
    border-top: 1px dashed #ebeef5;
    padding-top: 10px;
}

.cfg-subitem {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.cfg-subitem-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #606266;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cfg-subitem-header {
    font-size: 12px;
    color: #909399;
    margin-bottom: 6px;
}

.cfg-subitem-header span:not(.cfg-subitem-name) {
    width: 110px;
    text-align: center;
    flex-shrink: 0;
}

.cfg-subitem .el-input-number {
    width: 110px;
    flex-shrink: 0;
}

/* 尺寸多选下拉底部工具条：固定在下拉列表底部 */
.size-select-footer {
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 12px;
    border-top: 1px solid #ebeef5;
    background: #f5f7fa;
}

.size-select-count {
    font-size: 12px;
    color: #909399;
}

.size-select-actions .el-button {
    padding: 0;
    margin-left: 10px;
    font-size: 12px;
}

.size-clear-btn {
    color: #f56c6c;
}

/* 配置弹窗内容过高时滚动 */
::v-deep .cfg-dialog .el-dialog__body {
    max-height: 62vh;
    overflow-y: auto;
}

.pagination {
    margin-top: 20px;
    text-align: right;
}

::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
    background-color: #1890ff;
}
</style>
